import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import Collapse from '@material-ui/core/Collapse'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Typography from '@material-ui/core/Typography'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormGroup from '@material-ui/core/FormGroup'
import FormLabel from '@material-ui/core/FormLabel'
import Checkbox from '@material-ui/core/Checkbox'
import Grid from '@material-ui/core/Grid'
import ProviderAPI from 'config/api/ProviderAPI'
import { onError } from 'config/lib/errorLib'
import componentStyles from 'assets/theme/views/admin/react-bs-table.js'
import { NotificationManager } from 'react-notifications'
import Loader from 'components/general/Loader'
import MissingInfoDialog from './MissingInfoDialog'

const useStyles = makeStyles(componentStyles)

// Selectable summary bullets. `product` fields only appear when that product is
// in the provider's enabledProducts.
const BULLET_FIELDS = [
    { key: 'yearEstablished', label: 'Year Established' },
    { key: 'panelWarranty', label: 'Solar Panel Warranty', product: 'Solar' },
    { key: 'performanceWarranty', label: 'Performance Warranty', product: 'Solar' },
    { key: 'insulationWarranty', label: 'Insulation Warranty', product: 'Insulation' },
    { key: 'evChargerWarranty', label: 'EV Charger Warranty', product: 'EV Charger' },
    { key: 'heatPumpWarranty', label: 'Heat Pump Warranty', product: 'Heat Pump' },
    { key: 'workmanshipWarranty', label: 'Workmanship Warranty' },
    { key: 'totalStaff', label: 'Total Staff' },
    { key: 'totalInstallations', label: 'Total Installations' },
    { key: 'annualInstallations', label: 'Annual Installations' },
    { key: 'officeLocations', label: 'Office Location(s)' },
    { key: 'publicLiabilityInsurance', label: 'Public Liability Insurance' },
    { key: 'professionalIndemnityInsurance', label: 'Professional Indemnity Insurance' },
    { key: 'dedicatedSupport', label: 'Dedicated Support' },
    { key: 'seaiApproved', label: 'SEAI approved' }
]

const MIN_BULLETS = 1
const MAX_BULLETS = 6

function EditRecommendationCard(props) {
    const classes = useStyles()
    const [open, setOpen] = useState(true)
    const api = new ProviderAPI()

    const isSolar = props.isSolar !== false
    const enabledProducts = props.enabledProducts || []
    const visibleFields = BULLET_FIELDS.filter(f => {
        if (!f.product) return true
        if (f.product === 'Solar') return enabledProducts.includes('Solar') || isSolar
        return enabledProducts.includes(f.product)
    })

    const bullets = props.details?.bullets || BULLET_FIELDS.reduce((acc, f) => {
        acc[f.key] = false
        return acc
    }, {})


    const [loading, setLoading] = useState(false)
    const [showMissingFields, setShowMissingFields] = useState(false)
    const [missingFields, setMissingFields] = useState([])

    function countBullets() {
        return Object.values(bullets).filter(Boolean).length
    }

    function getMissingFields() {
        const count = countBullets()
        if (count < MIN_BULLETS || count > MAX_BULLETS) {
            return [{ key: 'bullets', label: `Select between ${MIN_BULLETS} and ${MAX_BULLETS} summary bullets` }]
        }
        return []
    }

    function checkRequiredFields() {
        const count = countBullets()
        return count < MIN_BULLETS || count > MAX_BULLETS
    }

    function updateCheckbox(name, value) {
        const selected = countBullets()
        if (!value || (value && selected < MAX_BULLETS)) {
            const updated = { ...bullets, [name]: value }
            props.updateField('profileDetails.bullets', updated)
        } else {
            NotificationManager.error(`You can only select ${MAX_BULLETS} bullets`)
        }
    }

    async function saveDetails(showDialogOnError = false) {
        setLoading(true)
        const count = countBullets()
        if (count >= MIN_BULLETS && count <= MAX_BULLETS) {
            try {
                const updatedCompany = await api.updateProfileBullets(props.companyId, bullets)
                props.updateCompany(updatedCompany)
                NotificationManager.success('Settings saved')
                setLoading(false)
                return true
            } catch (e) {
                onError(e)
            }
        } else {
            if (showDialogOnError) {
                setMissingFields(getMissingFields())
                setShowMissingFields(true)
            } else {
                NotificationManager.error(`You must select between ${MIN_BULLETS} and ${MAX_BULLETS} bullets`)
            }
        }
        setLoading(false)
        return false
    }

    async function previewCard() {
        const valid = await saveDetails(true)
        if (valid) {
            const previewUrl = `https://energyefficiency.ie/preview-card?slug=${props.companyId}`
            window.open(previewUrl, '_blank', 'noopener,noreferrer')
        }
    }

    async function nextStage() {
        const valid = await saveDetails(true)
        if (valid && props.updateStage) {
            props.updateStage('aboutCompany')
        }
    }

    return (
        <Card>
            <CardHeader
                subheader={
                    <>
                        <Box display='flex' style={{justifyContent: 'space-between'}}>
                            <Box>
                                <Box
                                    component={Typography}
                                    variant='h2'
                                    marginBottom='0!important'
                                >
                                    <Box component='span'>Customize Summary</Box>
                                </Box>
                                <Box
                                    component='p'
                                    fontSize='.875rem'
                                    marginBottom='0'
                                    marginTop='0'
                                    lineHeight='1.7'
                                    fontWeight='300'
                                >
                                    This is the profile summary customers will see when we recommend your company
                                </Box>
                            </Box>
                        </Box>
                    </>
                }
                onClick={() => setOpen(o => !o)}
                style={{ cursor: 'pointer' }}
                action={<ExpandMoreIcon style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', marginTop: 4 }} />}
                classes={{ root: classes.cardHeaderRoot }}
            ></CardHeader>
            <Collapse in={open}>
            <CardContent>
                <Grid container>
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormLabel>Customize your profile summary</FormLabel>
                            <Grid container spacing={2}>
                                {[
                                    visibleFields.slice(0, Math.ceil(visibleFields.length / 2)),
                                    visibleFields.slice(Math.ceil(visibleFields.length / 2))
                                ].map((column, columnIndex) => (
                                    <Grid item xs={12} sm={6} key={columnIndex}>
                                        <FormControl component={Box} width='100%'>
                                            {column.map(field => (
                                                <FormControlLabel
                                                    key={field.key}
                                                    control={
                                                        <Checkbox
                                                            checked={!!bullets[field.key]}
                                                            onChange={(e) => updateCheckbox(field.key, e.target.checked)}
                                                            color='primary'
                                                        />
                                                    }
                                                    label={<p style={{ margin: 0, padding: 0 }}>{field.label}</p>}
                                                />
                                            ))}
                                        </FormControl>
                                    </Grid>
                                ))}
                            </Grid>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12}>
                        <Box display='flex' justifyContent='center' width='100%'>
                            <Button
                                variant='outlined'
                                color='primary'
                                disabled={loading}
                                onClick={previewCard}
                                style={{ marginRight: '0.5rem' }}
                            >
                                {!loading ? 'Preview Card' : 'Saving...'}
                            </Button>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Box display='flex' justifyContent='flex-end' width='100%'>
                            <Button
                                variant='contained'
                                color='primary'
                                disabled={loading}
                                onClick={nextStage}
                            >
                                {!loading ? <>{(props.admin || props.profile) ? 'Save' : 'Save & Continue'}</> : <Loader height={12} />}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
            </Collapse>
            <MissingInfoDialog
                open={showMissingFields}
                onClose={() => setShowMissingFields(false)}
                missingFields={missingFields}
            />
        </Card>
    )
}

export default EditRecommendationCard
